import React from 'react';
import {Moment} from 'moment';
import Button from '@material-ui/core/Button';
import Text from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import UserAvatar from 'components/pure/utils/UserAvatar';
import User from 'records/User';
import {RepMetricInfo} from '@store/@types/metric';
import {formatDuration} from 'utils/periodAsText';
import formatSecondsShort from 'utils/formatSecondsShort';
import CloseIcon from '@material-ui/icons/Close';

interface RepInfoPopupProps {
  onCreatePlan: () => void;
  onClose: () => void;
  endDate: Moment;
  startDate: Moment;
  rep: User;
  repMetricInfo: RepMetricInfo;
  showClickBlurb: boolean;
}

export const RepInfoPopup: React.FC<RepInfoPopupProps> = ({
  startDate,
  endDate,
  rep,
  repMetricInfo,
  onCreatePlan,
  onClose,
  showClickBlurb,
}) => {
  const css = useStyles();

  return (
    <div className={css.root}>
      <CloseIcon className={css.closeIcon} onClick={onClose} />
      <div className={css.header}>
        <div className={css.avatar}>
          <UserAvatar user={rep} />
        </div>
        <Text variant="h3">{rep.getFullName()}</Text>
      </div>
      <LineItem
        label={`Coaching in past ${formatDuration(startDate, endDate)}`}
        value={formatSecondsShort(repMetricInfo.coachingDuration * 60, false, true, false)}
      />
      <Divider className={css.primary} />
      <LineItem label="Best Skill" value={repMetricInfo.bestSkill} />
      <Divider className={css.best} />
      <LineItem label="Worst Skill" value={repMetricInfo.worstSkill} />
      <Divider className={css.worst} />
      {showClickBlurb && <Text className={css.clickBlurb}>Click on avatar for more details</Text>}
      {!repMetricInfo.hasPlan && (
        <div className={css.actions}>
          <Button fullWidth variant="contained" color="primary" onClick={onCreatePlan}>
            Create Plan
          </Button>
        </div>
      )}
    </div>
  );
};

interface LineItemProps {
  label: string;
  value: string;
}

function LineItem({label, value}: LineItemProps) {
  const css = useStyles();
  return (
    <>
      <div className={css.lineItem}>
        <Text className={css.label}>{label}: </Text>
        <Text className={css.value}>{value}</Text>
      </div>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  primary: {
    backgroundColor: theme.palette.primary.light,
  },
  best: {
    backgroundColor: theme.palette.success.light,
  },
  worst: {
    backgroundColor: theme.palette.error.light,
    marginBottom: 20,
  },
  clickBlurb: {
    textAlign: 'center',
    marginBottom: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
  },
  lineItem: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: 5,
  },
  label: {
    fontWeight: 'bold',
    marginRight: 10,
  },
  value: {},
  actions: {
    marginBottom: 20,
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(1),
    '& > div': {
      height: '100%',
    },
  },
  root: {
    maxWidth: 280,
    minWidth: 210,
    position: 'relative',
  },
  closeIcon: {
    position: 'absolute',
    top: 5,
    right: 5,
    cursor: 'pointer',
  },
}));
